// reducer that tracks whether the phone bar is visible, and the customer being called

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Call } from '@/types/validationSchemas/CallSchema';

// CallInfo can be either:
// 1. A partial object with just callSid (when set from Twilio SDK initially)
// 2. A full Call object from the backend (when synced with server)
type CallInfo = (Call & { callSid?: string }) | { callSid: string } | null;

export type ForwardingStatus =
	| null
	| 'initiated'
	| 'ringing'
	| 'answered'
	| 'failed'
	| 'canceled';

type NonNullForwardingStatus = Exclude<ForwardingStatus, null>;

const FORWARDING_STATUS_ORDER: Record<NonNullForwardingStatus, number> = {
	initiated: 1,
	ringing: 2,
	answered: 3,
	failed: 3,
	canceled: 3,
};

const TERMINAL_FORWARDING_STATUSES = new Set<NonNullForwardingStatus>([
	'answered',
	'failed',
	'canceled',
]);

export interface ForwardingTargetUser {
	_id: string;
	name: string;
}

interface VoiceState {
	visible: boolean;
	customer: any;
	callInfo: CallInfo;
	status: string;
	ongoingCalls: Call[];
	forwardingStatus: ForwardingStatus;
	forwardingTargetUser: ForwardingTargetUser | null;
}

const initialState: VoiceState = {
	visible: false,
	customer: null,
	callInfo: null,
	status: 'idle',
	ongoingCalls: [],
	forwardingStatus: null,
	forwardingTargetUser: null,
};

const voiceSlice = createSlice({
	name: 'voice',
	initialState,
	reducers: {
		setVisible: (state, action) => {
			state.visible = action.payload;
			if (!action.payload) {
				state.forwardingStatus = null;
				state.forwardingTargetUser = null;
			}
		},
		setCustomer: (state, action) => {
			state.customer = action.payload;
		},
		setCallInfo: (state, action) => {
			state.callInfo = action.payload;
		},
		setStatus: (state, action) => {
			state.status = action.payload;
			if (action.payload === 'idle') {
				state.forwardingStatus = null;
				state.forwardingTargetUser = null;
			}
		},
		setOngoingCalls: (state, action) => {
			state.ongoingCalls = action.payload;
		},
		setForwardingStatus: (
			state,
			action: PayloadAction<ForwardingStatus>,
		) => {
			const nextStatus = action.payload;
			if (!nextStatus) {
				state.forwardingStatus = null;
				return;
			}

			const currentStatus = state.forwardingStatus;
			if (currentStatus) {
				if (TERMINAL_FORWARDING_STATUSES.has(currentStatus)) {
					return;
				}
				if (
					FORWARDING_STATUS_ORDER[nextStatus] <
					FORWARDING_STATUS_ORDER[currentStatus]
				) {
					return;
				}
			}

			state.forwardingStatus = nextStatus;
		},
		setForwardingTargetUser: (
			state,
			action: PayloadAction<ForwardingTargetUser | null>,
		) => {
			state.forwardingTargetUser = action.payload;
		},
	},
});

export const {
	setVisible,
	setCustomer,
	setCallInfo,
	setStatus,
	setOngoingCalls,
	setForwardingStatus,
	setForwardingTargetUser,
} = voiceSlice.actions;

export default voiceSlice.reducer;
